/**
 * KPI, Site, Cross-Site & Material Cost Hooks (Phase 10)
 * Provides React Query hooks for KPI Dashboard, Multi-Site Management,
 * Cross-Site Analysis, and Material Cost tracking endpoints.
 *
 * Endpoints from AI-Planner Phase 10 (KPI + Multi-Site + Costs)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  kpiApi,
  sitesApi,
  crossSiteApi,
  materialCostsApi,
} from '../api/client';
import type {
  KPIRequest,
  OEEResponse,
  LeadTimeResponse,
  KPIUtilizationResponse,
  OTDResponse,
  KPISnapshotCreateRequest,
  KPISnapshot,
  KPISnapshotListItem,
  KPITrendResponse,
  KPISnapshotType,
  KPIMetric,
  Site,
  SiteListItem,
  CrossSiteRequest,
  CrossSiteConflictsResponse,
  MaterialCost,
  MaterialCostListItem,
  CostDashboardRequest,
  ScenarioCostRequest,
  OrderCostSummary,
} from '../types';

// ============ Query Key Constants ============

export const kpiQueryKeys = {
  oee: (params?: KPIRequest) => ['kpi', 'oee', params] as const,
  leadTime: (params?: KPIRequest) => ['kpi', 'lead-time', params] as const,
  utilization: (params?: KPIRequest) =>
    ['kpi', 'utilization', params] as const,
  otd: (params?: KPIRequest) => ['kpi', 'otd', params] as const,
  snapshots: ['kpi', 'snapshots'] as const,
  trend: (metric: string, params?: Record<string, unknown>) =>
    ['kpi', 'trend', metric, params] as const,
};

export const siteQueryKeys = {
  list: ['sites'] as const,
  detail: (id: string) => ['sites', id] as const,
};

export const crossSiteQueryKeys = {
  consolidated: (params?: CrossSiteRequest) =>
    ['cross-site', 'consolidated', params] as const,
  conflicts: (params?: CrossSiteRequest) =>
    ['cross-site', 'conflicts', params] as const,
};

export const materialCostQueryKeys = {
  list: ['material-costs'] as const,
  detail: (id: string) => ['material-costs', id] as const,
  dashboard: (params?: CostDashboardRequest) =>
    ['material-costs', 'dashboard', params] as const,
  byOrder: (orderId: string) =>
    ['material-costs', 'by-order', orderId] as const,
};

// ============ KPI Query Hooks ============

/** OEE analysis per work center for a date range */
export function useKPIOee(params: KPIRequest, enabled = true) {
  return useQuery<OEEResponse>({
    queryKey: kpiQueryKeys.oee(params),
    queryFn: () => kpiApi.oee(params),
    enabled,
    staleTime: 60000,
  });
}

/** Lead-time analysis for a date range */
export function useKPILeadTime(params: KPIRequest, enabled = true) {
  return useQuery<LeadTimeResponse>({
    queryKey: kpiQueryKeys.leadTime(params),
    queryFn: () => kpiApi.leadTime(params),
    enabled,
    staleTime: 60000,
  });
}

/** Work center utilization analysis */
export function useKPIUtilization(params: KPIRequest, enabled = true) {
  return useQuery<KPIUtilizationResponse>({
    queryKey: kpiQueryKeys.utilization(params),
    queryFn: () => kpiApi.utilization(params),
    enabled,
    staleTime: 60000,
  });
}

/** On-Time Delivery rate analysis */
export function useKPIOtd(params: KPIRequest, enabled = true) {
  return useQuery<OTDResponse>({
    queryKey: kpiQueryKeys.otd(params),
    queryFn: () => kpiApi.otd(params),
    enabled,
    staleTime: 60000,
  });
}

/** List all KPI snapshots */
export function useKPISnapshots(enabled = true) {
  return useQuery<KPISnapshotListItem[]>({
    queryKey: kpiQueryKeys.snapshots,
    queryFn: () => kpiApi.getSnapshots(),
    enabled,
    refetchInterval: 120000,
  });
}

/** KPI trend data for a specific metric over time */
export function useKPITrend(
  metric: KPIMetric,
  options?: {
    days?: number;
    snapshot_type?: KPISnapshotType;
    site_code?: string;
  },
  enabled = true
) {
  return useQuery<KPITrendResponse>({
    queryKey: kpiQueryKeys.trend(metric, options),
    queryFn: () =>
      kpiApi.getTrend({
        metric,
        days: options?.days,
        snapshot_type: options?.snapshot_type,
        site_code: options?.site_code,
      }),
    enabled,
    staleTime: 120000,
  });
}

// ============ KPI Mutation Hooks ============

/** Create a new KPI snapshot (triggers computation) */
export function useCreateKPISnapshot() {
  const queryClient = useQueryClient();
  return useMutation<KPISnapshot, Error, KPISnapshotCreateRequest>({
    mutationFn: (data) => kpiApi.createSnapshot(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kpi'] });
    },
  });
}

// ============ Site Hooks ============

/** List all sites */
export function useSites(enabled = true) {
  return useQuery<SiteListItem[]>({
    queryKey: siteQueryKeys.list,
    queryFn: () => sitesApi.list(),
    enabled,
    staleTime: 300000, // 5 min — sites rarely change
  });
}

/** Get single site detail */
export function useSite(id: string, enabled = true) {
  return useQuery<Site>({
    queryKey: siteQueryKeys.detail(id),
    queryFn: () => sitesApi.get(id),
    enabled: enabled && !!id,
  });
}

/** Create a new site */
export function useCreateSite() {
  const queryClient = useQueryClient();
  return useMutation<Site, Error, Partial<Site>>({
    mutationFn: (data) => sitesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteQueryKeys.list });
    },
  });
}

/** Update a site */
export function useUpdateSite() {
  const queryClient = useQueryClient();
  return useMutation<Site, Error, { id: string; data: Partial<Site> }>({
    mutationFn: ({ id, data }) => sitesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: siteQueryKeys.list });
      queryClient.invalidateQueries({
        queryKey: siteQueryKeys.detail(variables.id),
      });
    },
  });
}

/** Delete a site */
export function useDeleteSite() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => sitesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siteQueryKeys.list });
    },
  });
}

// ============ Cross-Site Hooks ============

/** Get consolidated cross-site KPI data */
export function useCrossSiteConsolidated(
  params: CrossSiteRequest,
  enabled = true
) {
  return useQuery<Record<string, unknown>>({
    queryKey: crossSiteQueryKeys.consolidated(params),
    queryFn: () => crossSiteApi.consolidated(params),
    enabled,
    staleTime: 60000,
  });
}

/** Detect cross-site conflicts */
export function useCrossSiteConflicts(
  params: CrossSiteRequest,
  enabled = true
) {
  return useQuery<CrossSiteConflictsResponse>({
    queryKey: crossSiteQueryKeys.conflicts(params),
    queryFn: () => crossSiteApi.conflicts(params),
    enabled,
    staleTime: 60000,
  });
}

// ============ Material Cost Hooks ============

/** List all material costs */
export function useMaterialCosts(enabled = true) {
  return useQuery<MaterialCostListItem[]>({
    queryKey: materialCostQueryKeys.list,
    queryFn: () => materialCostsApi.list(),
    enabled,
    refetchInterval: 120000,
  });
}

/** Get single material cost detail */
export function useMaterialCost(id: string, enabled = true) {
  return useQuery<MaterialCost>({
    queryKey: materialCostQueryKeys.detail(id),
    queryFn: () => materialCostsApi.get(id),
    enabled: enabled && !!id,
  });
}

/** Get cost dashboard summary */
export function useCostDashboard(
  params: CostDashboardRequest,
  enabled = true
) {
  return useQuery<Record<string, unknown>>({
    queryKey: materialCostQueryKeys.dashboard(params),
    queryFn: () => materialCostsApi.dashboard(params),
    enabled,
    staleTime: 60000,
  });
}

/** Get cost forecast for a scenario */
export function useScenarioCostForecast() {
  return useMutation<Record<string, unknown>, Error, ScenarioCostRequest>({
    mutationFn: (data) => materialCostsApi.scenarioForecast(data),
  });
}

/** Get order-level cost summary */
export function useOrderCostSummary(orderId: string, enabled = true) {
  return useQuery<OrderCostSummary>({
    queryKey: materialCostQueryKeys.byOrder(orderId),
    queryFn: () => materialCostsApi.byOrder(orderId),
    enabled: enabled && !!orderId,
    staleTime: 60000,
  });
}

// ============ Helper Functions ============

/** Color coding for OEE values */
export function getOEEColor(value: number): string {
  if (value >= 85) return 'text-green-600';
  if (value >= 60) return 'text-amber-600';
  return 'text-red-600';
}

/** Color coding for OTD rate */
export function getOTDColor(rate: number): string {
  if (rate >= 95) return 'text-green-600';
  if (rate >= 85) return 'text-amber-600';
  return 'text-red-600';
}

/** Color coding for utilization percentage */
export function getUtilizationColor(pct: number): string {
  if (pct > 100) return 'text-red-600';
  if (pct >= 80) return 'text-green-600';
  if (pct >= 50) return 'text-amber-600';
  return 'text-gray-500';
}

/** Color coding for cost variance */
export function getCostVarianceColor(variancePct: number): string {
  if (variancePct <= -5) return 'text-green-600'; // Under budget
  if (variancePct <= 5) return 'text-gray-600'; // On budget
  if (variancePct <= 15) return 'text-amber-600'; // Slightly over
  return 'text-red-600'; // Significantly over
}

/** Format KPI metric name for display */
export function getKPIMetricLabel(metric: KPIMetric): string {
  const labels: Record<KPIMetric, string> = {
    oee_overall: 'OEE Overall',
    oee_availability: 'Availability',
    oee_performance: 'Performance',
    otd_rate: 'On-Time Delivery',
    avg_wc_utilization: 'Avg. Utilization',
    avg_lead_time_hours: 'Avg. Lead Time',
    median_lead_time_hours: 'Median Lead Time',
    total_orders_completed: 'Orders Completed',
  };
  return labels[metric] || metric;
}

/** Format percentage value */
export function formatPercent(value: number | string | null | undefined, decimals = 1): string {
  const num = typeof value === 'string' ? parseFloat(value) : (value ?? 0);
  if (isNaN(num)) return '0%';
  return `${num.toFixed(decimals)}%`;
}

/** Format currency value */
export function formatCurrency(
  value: number,
  currency = 'EUR'
): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
  }).format(value);
}
